import { useCallback } from 'react'
import { DndContext, DragOverlay, MeasuringStrategy, AutoScrollActivator } from '@dnd-kit/core'
import { Kbd, KbdGroup } from '@/components/ui/kbd'
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers'
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { StatusSection } from './task-list'
import { TodoItem } from './todo-item'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuLabel,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
} from '@/components/ui/context-menu'
import {
  ArrowUpDown,
  Calendar as CalendarIcon,
  Zap,
  CheckCircle2,
  X,
  Filter,
  AlertCircle,
  Clock,
  Layers,
  ChevronUp,
  ChevronDown,
  CheckCheck,
  ListTodo,
  LayoutPanelLeft,
  Plus,
  Maximize2,
  Minimize2,
} from 'lucide-react'
import { itemPresence, viewPresence } from './motion-presets'

const scopeSpring = {
  type: 'spring',
  stiffness: 260,
  damping: 30,
  mass: 0.86,
}

const resolveAddTaskInputRef = (status, todoQuickAddRef, doneQuickAddRef) => {
  if (status === 'todo') return todoQuickAddRef
  if (status === 'done') return doneQuickAddRef
  return undefined
}

const resolveListStatuses = (statusScope, searchQuery, kanbanStatuses, allStatuses) => {
  const sourceStatuses = searchQuery ? kanbanStatuses : allStatuses
  const supportedStatuses = Array.from(
    new Set(sourceStatuses.filter((status) => status === 'todo' || status === 'done')),
  )

  if (statusScope === 'todo' || statusScope === 'done') {
    return supportedStatuses.filter((status) => status === statusScope)
  }

  return supportedStatuses
}

const ScopeStatusSections = ({
  isDragActive,
  statusScope,
  scrollContainerRef,
  handleClearSelectedIds,
  ...props
}) => (
  <AnimatePresence mode="popLayout" initial={false}>
    <motion.div
      key={`scope-${statusScope}`}
      layout="position"
      {...viewPresence}
      transition={{
        ...viewPresence.transition,
        layout: scopeSpring,
      }}
      className="flex-1 min-h-0"
      data-dnd-active={isDragActive}
    >
      <StatusSectionList
        {...props}
        statusScope={statusScope}
        scrollContainerRef={scrollContainerRef}
        handleClearSelectedIds={handleClearSelectedIds}
      />
    </motion.div>
  </AnimatePresence>
)

export function TaskListView(props) {
  const {
    handleDragCancel,
    sensors,
    collisionDetection,
    isDragActive,
    draggingIds,
    activeTodo,
    activeDragSize,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    toggleTodo,
    deleteTodo,
    updateTodo,
    handleItemSelect,
    duplicateTodo,
    setEditingId,
    selectedListId,
    statusScope,
    scrollContainerRef,
    handleClearSelectedIds,
    anyExpanded,
  } = props
  const handleDragCancelRestore = useCallback(() => {
    handleDragCancel(true)
  }, [handleDragCancel])
  return (
    <motion.div
      key={`list-${selectedListId}`}
      layout="position"
      {...viewPresence}
      transition={{
        ...viewPresence.transition,
        layout: scopeSpring,
      }}
      className="h-full min-h-0 flex flex-col outline-none"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
        autoScroll={{
          enabled: true,
          activator: AutoScrollActivator.Pointer,
          acceleration: 12,
          interval: 5,
          threshold: {
            x: 0.1,
            y: 0.25,
          },
          layoutShiftCompensation: true,
        }}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.WhileDragging,
          },
        }}
        accessibility={{
          restoreFocus: true,
        }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancelRestore}
        onDragAbort={handleDragCancelRestore}
      >
        <ScopeStatusSections
          {...props}
          isDragActive={isDragActive}
          statusScope={statusScope}
          scrollContainerRef={scrollContainerRef}
          handleClearSelectedIds={handleClearSelectedIds}
          anyExpanded={anyExpanded}
        />
        <ListDragOverlay
          activeTodo={activeTodo}
          activeDragSize={activeDragSize}
          draggingIds={draggingIds}
          toggleTodo={toggleTodo}
          deleteTodo={deleteTodo}
          updateTodo={updateTodo}
          handleItemSelect={handleItemSelect}
          duplicateTodo={duplicateTodo}
          setEditingId={setEditingId}
          selectedListId={selectedListId}
        />
      </DndContext>
    </motion.div>
  )
}
function StatusSectionItem({
  st,
  itemsByStatus,
  todoMap,
  uiSettings,
  toggleTodo,
  deleteTodo,
  updateTodo,
  handleItemSelect,
  duplicateTodo,
  selectedIds,
  editingId,
  setEditingId,
  sectionColors,
  statusColorHandlers,
  setIsColorPickerOpen,
  recentlyDuplicatedIds,
  newTaskId,
  isSelecting,
  draggingIds,
  isDragActive,
  dragSizes,
  registerItem,
  statusQuickAddHandlers,
  selectedListId,
  todoQuickAddRef,
  doneQuickAddRef,
  scrollContainerRef,
  sortBy,
  handleSortChange,
  handleClearSort,
  filterBy,
  handleFilterChange,
  handleClearFilter,
  handleToggleAllExpanded,
  handleToggleAllDone,
  anyExpanded,
  hasTodoTasks,
  hasDoneTasks,
  todosLength,
  handleToggleSidebar,
  handleCreateStart,
  handleClearSelectedIds,
  sectionClassName,
}) {
  const idsForStatus = st === 'done' ? itemsByStatus.done : itemsByStatus.todo
  const todosForStatus = idsForStatus.map((id) => todoMap.get(id)).filter((todo) => Boolean(todo))
  const noopColor = useCallback(() => {}, [])
  const noopQuickAdd = useCallback(() => {}, [])
  const addTaskInputRef = resolveAddTaskInputRef(st, todoQuickAddRef, doneQuickAddRef)
  return (
    <motion.div
      layout="position"
      key={st}
      {...itemPresence}
      transition={{
        ...itemPresence.transition,
        layout: scopeSpring,
      }}
    >
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <StatusSectionTrigger
            st={st}
            uiSettings={uiSettings}
            todosForStatus={todosForStatus}
            toggleTodo={toggleTodo}
            deleteTodo={deleteTodo}
            updateTodo={updateTodo}
            handleItemSelect={handleItemSelect}
            duplicateTodo={duplicateTodo}
            selectedIds={selectedIds}
            editingId={editingId}
            setEditingId={setEditingId}
            sectionColors={sectionColors}
            statusColorHandlers={statusColorHandlers}
            setIsColorPickerOpen={setIsColorPickerOpen}
            recentlyDuplicatedIds={recentlyDuplicatedIds}
            newTaskId={newTaskId}
            isSelecting={isSelecting}
            draggingIds={draggingIds}
            isDragActive={isDragActive}
            dragSizes={dragSizes}
            registerItem={registerItem}
            statusQuickAddHandlers={statusQuickAddHandlers}
            selectedListId={selectedListId}
            addTaskInputRef={addTaskInputRef}
            noopColor={noopColor}
            noopQuickAdd={noopQuickAdd}
            scrollContainerRef={scrollContainerRef}
            handleClearSelectedIds={handleClearSelectedIds}
            sectionClassName={sectionClassName}
            anyExpanded={anyExpanded}
          />
        </ContextMenuTrigger>
        <StatusSectionContextMenuContent
          st={st}
          sortBy={sortBy}
          handleSortChange={handleSortChange}
          handleClearSort={handleClearSort}
          filterBy={filterBy}
          handleFilterChange={handleFilterChange}
          handleClearFilter={handleClearFilter}
          handleToggleAllExpanded={handleToggleAllExpanded}
          handleToggleAllDone={handleToggleAllDone}
          anyExpanded={anyExpanded}
          todosLength={todosLength}
          hasTodoTasks={hasTodoTasks}
          hasDoneTasks={hasDoneTasks}
          handleToggleSidebar={handleToggleSidebar}
          handleCreateStart={handleCreateStart}
        />
      </ContextMenu>
    </motion.div>
  )
}
function StatusSectionContextMenuContent({
  st,
  sortBy,
  handleSortChange,
  handleClearSort,
  filterBy,
  handleFilterChange,
  handleClearFilter,
  handleToggleAllExpanded,
  handleToggleAllDone,
  anyExpanded,
  todosLength,
  hasTodoTasks,
  hasDoneTasks,
  handleToggleSidebar,
  showSidebar,
  handleCreateStart,
}) {
  const handleCreateTaskClick = useCallback(() => {
    handleCreateStart(st)
  }, [handleCreateStart, st])
  return (
    <ContextMenuContent className="w-64">
      {/* Creation */}
      <ContextMenuItem onClick={handleCreateTaskClick}>
        <Plus className="mr-2 h-4 w-4" />
        <span>Add task here</span>
        <ContextMenuShortcutKeys keys={['N']} />
      </ContextMenuItem>

      <ContextMenuItem onClick={handleToggleAllDone} disabled={!hasTodoTasks && !hasDoneTasks}>
        {hasTodoTasks ? (
          <CheckCircle2 className="mr-2 h-4 w-4" />
        ) : (
          <RotateCcw className="mr-2 h-4 w-4" />
        )}
        <span>{hasTodoTasks ? 'Complete all' : 'Reopen all'}</span>
        <ContextMenuShortcutKeys keys={['⌘', hasTodoTasks ? 'D' : 'T']} />
      </ContextMenuItem>

      <ContextMenuSeparator />

      {/* Sorting & Filtering */}
      <ContextMenuSub>
        <ContextMenuSubTrigger>
          <ArrowUpDown className="mr-2 h-4 w-4" />
          <span>Sort by</span>
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className="w-48">
          <SortMenuItems
            sortBy={sortBy}
            handleSortChange={handleSortChange}
            handleClearSort={handleClearSort}
          />
        </ContextMenuSubContent>
      </ContextMenuSub>

      <ContextMenuSub>
        <ContextMenuSubTrigger>
          <Filter className="mr-2 h-4 w-4" />
          <span>Filter by</span>
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className="w-48">
          <FilterMenuItems
            filterBy={filterBy}
            handleFilterChange={handleFilterChange}
            handleClearFilter={handleClearFilter}
          />
        </ContextMenuSubContent>
      </ContextMenuSub>

      <ContextMenuSeparator />

      {/* View Options */}
      <ContextMenuItem onClick={handleToggleAllExpanded}>
        {anyExpanded ? (
          <Minimize2 className="mr-2 h-4 w-4" />
        ) : (
          <Maximize2 className="mr-2 h-4 w-4" />
        )}
        <span>{anyExpanded ? 'Collapse all' : 'Expand all'}</span>
      </ContextMenuItem>

      <ContextMenuItem onClick={handleToggleSidebar}>
        <LayoutPanelLeft className="mr-2 h-4 w-4" />
        <span>{showSidebar ? 'Hide Sidebar' : 'Show Sidebar'}</span>
        <ContextMenuShortcutKeys keys={['M']} />
      </ContextMenuItem>
    </ContextMenuContent>
  )
}
const ContextMenuShortcutKeys = ({ keys }) => (
  <ContextMenuShortcut>
    <KbdGroup>
      {keys.map((key) => (
        <Kbd key={key}>{key}</Kbd>
      ))}
    </KbdGroup>
  </ContextMenuShortcut>
)
function StatusSectionList(props) {
  const { searchQuery, statusScope, kanbanStatuses, allStatuses } = props
  const listStatuses = resolveListStatuses(statusScope, searchQuery, kanbanStatuses, allStatuses)
  const sectionClassName = listStatuses.length === 1 ? 'h-full min-h-0' : 'flex-1 min-h-0'
  return (
    <LayoutGroup id="list-status-sections">
      <motion.div
        layout="position"
        transition={scopeSpring}
        className="flex-1 min-h-0 flex flex-col gap-4"
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {listStatuses.map((st) => (
            <StatusSectionItem
              key={st}
              st={st}
              {...props}
              sectionClassName={sectionClassName}
              handleToggleSidebar={props.handleToggleSidebar}
              handleCreateStart={props.handleCreateStart}
              handleClearSelectedIds={props.handleClearSelectedIds}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </LayoutGroup>
  )
}
function StatusSectionTrigger({
  st,
  uiSettings,
  todosForStatus,
  toggleTodo,
  deleteTodo,
  updateTodo,
  handleItemSelect,
  duplicateTodo,
  selectedIds,
  editingId,
  setEditingId,
  sectionColors,
  statusColorHandlers,
  setIsColorPickerOpen,
  recentlyDuplicatedIds,
  newTaskId,
  isSelecting,
  draggingIds,
  isDragActive,
  dragSizes,
  registerItem,
  statusQuickAddHandlers,
  selectedListId,
  addTaskInputRef,
  noopColor,
  noopQuickAdd,
  scrollContainerRef,
  handleClearSelectedIds,
  sectionClassName,
  anyExpanded,
  ...props
}) {
  const sectionColor = st === 'done' ? sectionColors.done : sectionColors.todo
  return (
    <StatusSection
      {...props}
      uiSettings={uiSettings}
      className={cn('transition-opacity duration-300', sectionClassName)}
      status={st}
      containerId={st}
      todos={todosForStatus}
      onToggle={toggleTodo}
      onDelete={deleteTodo}
      onUpdate={updateTodo}
      onSelect={handleItemSelect}
      onDuplicate={duplicateTodo}
      selectedIds={selectedIds}
      editingId={editingId}
      setEditingId={setEditingId}
      color={sectionColor}
      onColorChange={statusColorHandlers.get(st) ?? noopColor}
      onColorPickerOpenChange={setIsColorPickerOpen}
      recentlyDuplicatedIds={recentlyDuplicatedIds}
      displayStatus={st.charAt(0).toUpperCase() + st.slice(1)}
      newTaskId={newTaskId}
      isSelecting={isSelecting}
      draggingIds={draggingIds}
      isDragging={isDragActive}
      dragSizes={dragSizes}
      registerItem={registerItem}
      onQuickAdd={statusQuickAddHandlers.get(st) ?? noopQuickAdd}
      showListBadge={selectedListId === 'all'}
      showHeader={false}
      addTaskInputRef={addTaskInputRef}
      scrollContainerRef={scrollContainerRef}
      handleClearSelectedIds={handleClearSelectedIds}
      anyExpanded={anyExpanded}
    />
  )
}
function ListDragOverlay({
  activeTodo,
  activeDragSize,
  draggingIds,
  toggleTodo,
  deleteTodo,
  updateTodo,
  handleItemSelect,
  duplicateTodo,
  setEditingId,
  selectedListId,
}) {
  return (
    <DragOverlay dropAnimation={null} adjustScale={false}>
      {activeTodo ? (
        <div
          className="relative pointer-events-none z-[80]"
          data-dnd-item
          style={
            activeDragSize
              ? {
                  width: activeDragSize.width,
                  height: activeDragSize.height,
                }
              : undefined
          }
        >
          <div
            className={`bg-card border rounded-lg shadow-2xl cursor-grabbing ${draggingIds.size > 1 ? 'relative' : ''}`}
          >
            <TodoItem
              todo={activeTodo}
              isSelected={false}
              isEditing={false}
              disableInteractions
              onToggle={toggleTodo}
              onDelete={deleteTodo}
              onUpdate={updateTodo}
              onSelect={handleItemSelect}
              onDuplicate={duplicateTodo}
              setEditingId={setEditingId}
              showListBadge={selectedListId === 'all'}
            />
            {draggingIds.size > 1 && (
              <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full shadow-md z-50">
                {draggingIds.size}
              </div>
            )}
          </div>
          {draggingIds.size > 1 && (
            <>
              <div className="absolute top-1 left-1 w-full h-full bg-card border rounded-lg shadow-sm -z-10 rotate-[-1deg] opacity-70" />
              <div className="absolute top-2 left-2 w-full h-full bg-card border rounded-lg shadow-sm -z-20 rotate-[-2deg] opacity-40" />
            </>
          )}
        </div>
      ) : null}
    </DragOverlay>
  )
}
function StatusSectionSortMenu({ sortBy, handleSortChange, handleClearSort }) {
  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger>
        <ArrowUpDown className="mr-2 h-4 w-4" />
        <span>Sort</span>
      </ContextMenuSubTrigger>
      <ContextMenuSubContent className="w-44">
        <ContextMenuRadioGroup value={sortBy || ''} onValueChange={handleSortChange}>
          <SortMenuItems handleClearSort={handleClearSort} />
        </ContextMenuRadioGroup>
      </ContextMenuSubContent>
    </ContextMenuSub>
  )
}
const SortMenuItems = ({ handleClearSort }) => (
  <>
    <ContextMenuRadioItem value="date">
      <CalendarIcon className="h-4 w-4" />
      <span>Date</span>
    </ContextMenuRadioItem>
    <ContextMenuRadioItem value="priority">
      <Zap className="h-4 w-4" />
      <span>Priority</span>
    </ContextMenuRadioItem>
    <ContextMenuRadioItem value="status">
      <CheckCircle2 className="h-4 w-4" />
      <span>Status</span>
    </ContextMenuRadioItem>
    <ContextMenuSeparator />
    <ContextMenuItem onClick={handleClearSort}>
      <X className="h-4 w-4" />
      <span>Default</span>
    </ContextMenuItem>
  </>
)
function StatusSectionFilterMenu({ filterBy, handleFilterChange, handleClearFilter }) {
  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger>
        <Filter className="mr-2 h-4 w-4" />
        <span>Filter</span>
      </ContextMenuSubTrigger>
      <ContextMenuSubContent className="w-44">
        <ContextMenuRadioGroup value={filterBy || ''} onValueChange={handleFilterChange}>
          <FilterMenuItems handleClearFilter={handleClearFilter} />
        </ContextMenuRadioGroup>
      </ContextMenuSubContent>
    </ContextMenuSub>
  )
}
const FilterMenuItems = ({ handleClearFilter }) => (
  <>
    <ContextMenuRadioItem value="overdue">
      <AlertCircle className="h-4 w-4 text-destructive" />
      <span>Overdue</span>
    </ContextMenuRadioItem>
    <ContextMenuRadioItem value="today">
      <Clock className="h-4 w-4" />
      <span>Today</span>
    </ContextMenuRadioItem>
    <ContextMenuRadioItem value="tomorrow">
      <CalendarIcon className="h-4 w-4" />
      <span>Tomorrow</span>
    </ContextMenuRadioItem>
    <ContextMenuSeparator />
    <ContextMenuItem onClick={handleClearFilter}>
      <Layers className="h-4 w-4" />
      <span>Reset</span>
    </ContextMenuItem>
  </>
)
